import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './src/index.css';
import App from './App';

// Initialize Sentry before React renders
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    // Session Replay
    replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always record sessions with errors
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const SentryErrorFallback = ({ error }: { error: Error }) => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900 p-4">
    <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6">
      <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
        Something went wrong
      </h1>
      <p className="text-slate-600 dark:text-slate-400 mb-6">
        We encountered an unexpected error. Don't worry, your data is safe.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="w-full px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-md hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
      >
        Reload Page
      </button>
    </div>
  </div>
);

const AppWrapper = sentryDsn ? (
  <Sentry.ErrorBoundary fallback={SentryErrorFallback}>
    <App />
  </Sentry.ErrorBoundary>
) : (
  <App />
);

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    {AppWrapper}
  </React.StrictMode>
);